import { DateTime } from 'luxon'
import { computed, Ref, ref, watch } from 'vue'
import PointOfSaleService from '@/services/PointOfSaleService'
import PointOfSale from '@/types/PointOfSale'
import Producer from '@/types/Producer'
import { DataTableFilter } from '@/types/DataTable'

interface PointOfSaleFilterPayload {
  producer: Ref<Producer | null>
  label: string
  field: string
  multiple?: boolean
  queryParams?: Record<string, string>
}

export function usePointOfSaleFilter({ producer, label, field, multiple, queryParams }: PointOfSaleFilterPayload): DataTableFilter<string, PointOfSale> {
  const model: Ref<string | null> = ref(null)

  const loading = ref(false)

  const ready = Promise.withResolvers<boolean>()

  const expires = ref<DateTime>()

  const items: Ref<PointOfSale[]> = ref([])

  const load = async () => {
    if (producer.value === null) return

    loading.value = true

    const service = new PointOfSaleService()

    const response = await service.index(1, new URLSearchParams(queryParams).toString(), 10)

    items.value = response.data.data

    expires.value = DateTime.now().plus({ minutes: 5 })

    ready.resolve(true)

    loading.value = false
  }

  watch(producer, () => load(), { immediate: true })

  return {
    model,
    value: computed(() => model.value),
    items: computed(
      () => {
        if (expires.value !== undefined && DateTime.now() > expires.value) {
          load()
        }

        return items.value.map(i => ({ title: i.name, value: i.id }))
      },
    ),
    resources: computed(({
      get() {
        return items.value
      },
      set(_items: PointOfSale[]) {
        items.value = _items
      },
    })),
    label,
    field,
    type: 'autocomplete',
    loading,
    multiple,
    required: false,
    ready: ready.promise,
  }
}
